import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  notificationItems,
  notificationTypes,
  notificationCategories,
  notificationDashboardStats,
  notificationActions,
  getUnreadNotifications,
  getNotificationsByCategory,
  getPinnedNotifications,
  getTypeById,
  getCategoryById,
  type NotificationItem,
  type NotificationCategoryId,
} from '@/mocks/rfwNotificationCenter';

function NotifCard({ item }: { item: NotificationItem }) {
  const typeInfo = getTypeById(item.type);
  const categoryInfo = getCategoryById(item.category);
  return (
    <div className={`rounded-2xl p-4 border transition-all duration-200 group cursor-pointer ${
      item.isRead
        ? 'bg-background-50 border-background-200/70 hover:border-foreground-300/50'
        : 'bg-background-50 border-accent-500/30 ring-1 ring-accent-100'
    }`}>
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${typeInfo ? typeInfo.color.replace('text-', 'bg-').replace('600', '100') : 'bg-background-100'}`}>
            <i className={`${typeInfo?.icon || 'ri-notification-3-line'} ${typeInfo?.color || 'text-foreground-600'} text-lg`}></i>
          </div>
          {!item.isRead && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-accent-500 border-2 border-white"></span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm font-bold text-foreground-950 line-clamp-1">{item.title}</p>
            <span className="text-[10px] text-foreground-400 whitespace-nowrap shrink-0">{item.timestampRelative}</span>
          </div>
          <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed mb-2">{item.preview}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {typeInfo && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${typeInfo.color.replace('text-', 'bg-').replace('600', '100')} ${typeInfo.color}`}>
                <i className={`${typeInfo.icon} text-[9px]`}></i>
                {typeInfo.name}
              </span>
            )}
            {categoryInfo && (
              <span className="inline-flex items-center gap-1 text-[10px] text-foreground-400">
                <i className={`${categoryInfo.icon} text-[9px]`}></i>
                {categoryInfo.name}
              </span>
            )}
            {item.isPinned && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-600 text-[9px] font-semibold ml-auto">
                <i className="ri-pushpin-line text-[8px]"></i>
                Fijado
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-background-100">
            <Link
              to={item.actionUrl}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-[10px] font-semibold transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-eye-line text-[9px]"></i>
              {item.actionLabel}
            </Link>
            <span className="text-[10px] text-foreground-400 ml-auto flex items-center gap-1">
              <img src={item.iconUrl} alt={item.sourceName} className="w-4 h-4 rounded-full object-cover object-top" />
              {item.sourceName}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CentroNotificacionesSection() {
  const [activeTab, setActiveTab] = useState<'recientes' | 'categorias' | 'sin-leer'>('recientes');
  const unreadItems = getUnreadNotifications().slice(0, 8);
  const pinnedItems = getPinnedNotifications();

  const tabs = [
    { id: 'recientes' as const, label: 'Recientes', icon: 'ri-time-line' },
    { id: 'categorias' as const, label: 'Por Categoría', icon: 'ri-price-tag-3-line' },
    { id: 'sin-leer' as const, label: 'Sin Leer', icon: 'ri-mail-unread-line', badge: notificationDashboardStats.totalSinLeer },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
            CENTRO DE NOTIFICACIONES
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Cada Notificación Te Lleva <span className="text-accent-600">Directo a la Acción</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Radio en vivo, programas, concursos, eventos, comentarios y más. Todo lo que importa de tu comunidad radial, en un solo lugar. Un toque y estás donde necesitás estar.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Total Notificaciones', value: (notificationDashboardStats.totalNotificaciones / 1000).toFixed(1) + 'K', icon: 'ri-notification-3-line', color: 'text-accent-600 bg-accent-50' },
            { label: 'Hoy', value: notificationDashboardStats.totalHoy.toString(), icon: 'ri-calendar-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Sin Leer', value: notificationDashboardStats.totalSinLeer.toString(), icon: 'ri-mail-unread-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Tasa Apertura', value: notificationDashboardStats.tasaApertura + '%', icon: 'ri-eye-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Más Frecuente', value: notificationDashboardStats.masFrecuente, sub: notificationDashboardStats.masFrecuenteCount.toLocaleString(), icon: 'ri-bar-chart-line', color: 'text-amber-600 bg-amber-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
                {stat.sub && <p className="text-[9px] text-foreground-400">{stat.sub}</p>}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer relative ${
                  activeTab === tab.id
                    ? 'bg-accent-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-accent-100 text-accent-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'recientes' && (
          <div className="space-y-5">
            {pinnedItems.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Fijadas</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {pinnedItems.map((item) => (
                    <NotifCard key={item.id} item={item} />
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3">Últimas notificaciones</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {notificationItems.filter(n => !n.isPinned).slice(0, 6).map((item) => (
                  <NotifCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'categorias' && (
          <div className="space-y-6">
            {notificationCategories.slice(0, 5).map((cat) => {
              const catNotifs = getNotificationsByCategory(cat.id).slice(0, 2);
              if (catNotifs.length === 0) return null;
              return (
                <div key={cat.id}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-7 h-7 rounded-lg ${cat.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
                      <i className={`${cat.icon} ${cat.color} text-sm`}></i>
                    </div>
                    <h4 className="text-sm font-bold text-foreground-950">{cat.name}</h4>
                    <span className="text-[10px] text-foreground-400">{cat.totalCount.toLocaleString()} total</span>
                    {cat.unreadCount > 0 && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold ${cat.badgeColor} text-white`}>
                        {cat.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {catNotifs.map((item) => (
                      <NotifCard key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'sin-leer' && (
          <div className="space-y-4">
            {unreadItems.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 rounded-full bg-accent-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-2xl text-accent-500"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-950 mb-1">¡Todo al día!</p>
                <p className="text-xs text-foreground-500">No tenés notificaciones sin leer.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {unreadItems.map((item) => (
                  <NotifCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-10">
          <div className="p-5 rounded-2xl bg-accent-50 border border-accent-200/50">
            <h4 className="text-sm font-bold text-foreground-950 mb-3">¿Qué Podés Hacer?</h4>
            <div className="grid grid-cols-2 gap-3">
              {notificationActions.map((action) => (
                <div key={action.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-background-200/70">
                  <div className={`w-8 h-8 rounded-full ${action.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center shrink-0`}>
                    <i className={`${action.icon} ${action.color} text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-950">{action.name}</p>
                    <p className="text-[9px] text-foreground-500">{action.shortcut}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="p-5 rounded-2xl bg-background-100 border border-background-200/70">
            <h4 className="text-sm font-bold text-foreground-950 mb-3">Tipos de Notificación</h4>
            <div className="grid grid-cols-2 gap-2">
              {notificationTypes.map((type) => (
                <div key={type.id} className="flex items-center gap-2 p-2 rounded-xl bg-background-50 border border-background-200/70">
                  <div className={`w-6 h-6 rounded-md ${type.color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center shrink-0`}>
                    <i className={`${type.icon} ${type.color} text-xs`}></i>
                  </div>
                  <p className="text-[10px] font-medium text-foreground-700 leading-tight">{type.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/centro-notificaciones"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-notification-3-line"></i>
            Ir al Centro de Notificaciones
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-accent-100/70 border border-accent-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-flashlight-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Las notificaciones deben llevar directamente a la acción.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Cada notificación del Centro de Notificaciones tiene un botón de acción directa. Radio en vivo? Un toque y estás escuchando. Nuevo concurso? Un toque y estás participando. Te respondió un locutor? Un toque y ves el mensaje. Sin menús, sin pasos extra, sin perder tiempo buscando. La notificación es el puente directo entre vos y lo que importa.
          </p>
        </div>
      </div>
    </section>
  );
}